import gql from 'graphql-tag';
import React from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Navigation, LayoutContent, Layout } from '../../../components';
import { VenueInput } from './../../../globalTypes';
import { UpdateVenueQuery, UpdateVenueQueryVariables } from './__generated__/UpdateVenueQuery';
import { VenueQuery, VenueQueryVariables } from './__generated__/VenueQuery';
import { RevokeAccessMutation, RevokeAccessMutationVariables } from './__generated__/RevokeAccessMutation';
import { QueueSyncMutation, QueueSyncMutationVariables } from './__generated__/QueueSyncMutation';
import { ManageVenue } from './Manage';
import { useQueryParams } from '../../../hooks';

const venueFragment = gql`
  fragment VenueFields on Venue {
    id
    name
    address {
      id
      name
      street1
      city
      state
      zip
      phone
    }
    businessAddress {
      id
      name
      street1
      city
      state
      zip
      phone
    }
    ownerAddress {
      id
      name
      street1
      city
      state
      zip
      phone
    }
    paymentAddress {
      id
      name
      street1
      city
      state
      zip
      phone
    }
    ownerDOB
    pointOfContactFirstName
    pointOfContactLastName
    merchantId
    paymentProcessor
    businessRegistrationNumber
    liquorLicense
    itin
    name
    imageUrl
    config
    salesTaxPercentage
    tipPolicy
    requiredTipPercentage
    timezone
    posIntegration
    posConfig {
      menuLocationIds
      primaryLocationId
      menuId
    }
    posExternalId
    posSetupConfig
    posLastSyncTime
  }
`;

const query = gql`
  query VenueQuery($venueId: ID!) {
    venue(id: $venueId) {
      ...VenueFields
    }
    menus(venueId: $venueId) {
      id
      name
    }
  }
  ${venueFragment}
`;

const update = gql`
  mutation UpdateVenueQuery($venueId: ID!, $input: VenueInput!) {
    updateVenue(id: $venueId, input: $input) {
      ...VenueFields
    }
  }
  ${venueFragment}
`;

const revokeAccessMutation = gql`
  mutation RevokeAccessMutation($venueId: ID!, $posType: String!) {
    revokePOSAccess(id: $venueId, posType: $posType)
  }
`;

const queueSyncMutation = gql`
  mutation QueueSyncMutation($venueId: ID!) {
    queueSync(id: $venueId)
  }
`;

export const ManageContainer: React.FC<RouteComponentProps<{ venueId: string }>> = (props) => {
  const [onSubmit, submitData] = useMutation<UpdateVenueQuery, UpdateVenueQueryVariables>(update);
  const { loading, data, refetch, error } = useQuery<VenueQuery, VenueQueryVariables>(query, {
    variables: {
      venueId: props.match.params.venueId,
    },
  });

  const [revokePOSAccess, revokePOSAccessData] = useMutation<RevokeAccessMutation, RevokeAccessMutationVariables>(
    revokeAccessMutation,
    {
      onCompleted() {
        refetch();
      },
    }
  );

  const [queueSync, queueSyncData] = useMutation<QueueSyncMutation, QueueSyncMutationVariables>(queueSyncMutation);

  const queryParams = useQueryParams();

  const [editing, setEditing] = React.useState<string | undefined>(undefined);
  const onSave = async (saveData: VenueInput) => {
    const { config, ...rest } = saveData;
    setEditing(undefined);

    await onSubmit({
      variables: {
        venueId: props.match.params.venueId,
        input: {
          ...rest,
          config: config
            ? {
                ...data!.venue.config,
                drinkUnitLimitPerPerson: config.drinkUnitLimitPerPerson,
              }
            : undefined,
        },
      },
    });
  };

  const onCancelEditing = () => {
    setEditing(undefined);
  };

  const squareCallbackOpen = !!queryParams.get('square-saved');
  const squareError = queryParams.get('square-error');

  return (
    <Layout>
      <Navigation />
      <Layout.TopBar>
        <Layout.TopBar.Left>
          <Layout.TopBar.Title>My Venue</Layout.TopBar.Title>
        </Layout.TopBar.Left>
      </Layout.TopBar>
      <LayoutContent error={error ?? submitData.error ?? null}>
        <ManageVenue
          venue={data ? data.venue : undefined}
          posSetupConfig={data ? data.venue.posSetupConfig : undefined}
          onSubmit={onSave}
          onCancelEditing={onCancelEditing}
          onStartEditing={setEditing}
          loading={loading || submitData.loading || revokePOSAccessData.loading}
          editing={editing}
          squareCallbackOpen={squareCallbackOpen}
          squareError={squareError}
          revokePOSAccess={revokePOSAccess}
          squareRevoked={revokePOSAccessData.data?.revokePOSAccess}
          menus={data?.menus}
          queueSync={queueSync}
          queueSyncStatus={queueSyncData}
        />
      </LayoutContent>
    </Layout>
  );
};
